import { type ApiResponse } from '@webapp/core/interfaces/api/ApiResponse'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IMileageRatePresetRepository } from '@webapp/modules/expense/business-logic/interfaces/IMileageRatePresetRepository'
import {
  type CreateMileageRatePresetInputData,
  type UpdateMileageRatePresetInputData,
} from '@webapp/modules/expense/business-logic/types'
import { type ExpenseMileageConfigurationConstructor } from '@webapp/modules/expense/entities/ExpenseMileageConfiguration'
import { type MileageRatePresetConstructor } from '@webapp/modules/expense/entities/MileageRatePreset'
import { type ExpenseMileageConfigurationApiModel } from '../../api-models/ExpenseMileageConfigurationApiModel'
import { type MileageRatePresetApiModel } from '../../api-models/MileageRatePresetApiModel'
import { mapCreateMileageRatePresetInputDataToApi } from '../../mappers/mapCreateMileageRatePresetInputDataToApi'
import { mapExpenseMileageConfigurationApiModel } from '../../mappers/mapExpenseMileageConfigurationApiModel'
import { mapMileageRatePresetApiModel } from '../../mappers/mapMileageRatePresetApiModel'
import { mapUpdateMileageRatePresetInputDataToApi } from '../../mappers/mapUpdateMileageRatePresetInputDataToApi'
import { type MileageRatePresetPayload } from '../../types/MileageRatePresetPayload'

interface Dependency {
  httpClient: IHttpClient
}

export class MileageRatePresetRepository implements IMileageRatePresetRepository {
  constructor(private readonly deps: Dependency) {}

  async list(): Promise<MileageRatePresetConstructor[]> {
    try {
      const res = await this.deps.httpClient.get<
        ApiResponse<MileageRatePresetApiModel[]>
      >('/api/v3/settings/expense-mileage-preset/', {
        params: { page_size: 999 },
      })

      return res.data.data.map(mapMileageRatePresetApiModel)
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async getMileageRatePreset(
    id: number
  ): Promise<MileageRatePresetConstructor> {
    try {
      const res = await this.deps.httpClient.get<
        ApiResponse<MileageRatePresetApiModel>
      >(`/api/v3/settings/expense-mileage-preset/${id}/`)
      return mapMileageRatePresetApiModel(res.data.data)
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async createMileageRatePreset(
    payload: CreateMileageRatePresetInputData
  ): Promise<MileageRatePresetConstructor> {
    const mappedPayload = mapCreateMileageRatePresetInputDataToApi(payload)
    try {
      const res = await this.deps.httpClient.post<
        ApiResponse<MileageRatePresetApiModel>,
        MileageRatePresetPayload
      >('/api/v3/settings/expense-mileage-preset/', mappedPayload)
      return mapMileageRatePresetApiModel(res.data.data)
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async updateMileageRatePreset(
    id: number,
    payload: UpdateMileageRatePresetInputData
  ): Promise<MileageRatePresetConstructor> {
    const mappedPayload = mapUpdateMileageRatePresetInputDataToApi(payload)
    try {
      const res = await this.deps.httpClient.put<
        ApiResponse<MileageRatePresetApiModel>,
        MileageRatePresetPayload
      >(`/api/v3/settings/expense-mileage-preset/${id}/`, mappedPayload)
      return mapMileageRatePresetApiModel(res.data.data)
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async deleteMileageRatePreset(id: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(
        `/api/v3/settings/expense-mileage-preset/${id}/`
      )
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async getExpenseMileageConfiguration(): Promise<ExpenseMileageConfigurationConstructor> {
    try {
      const res = await this.deps.httpClient.get<
        ApiResponse<ExpenseMileageConfigurationApiModel>
      >('/api/v3/kvstore/system-preferences/expense-mileage-configuration/')
      return mapExpenseMileageConfigurationApiModel(res.data.data)
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }
}
